/**
 * useWorkspaceSettings Hook
 * 
 * Fetches workspace settings (general settings and permissions) and subscribes to changes.
 * Caches dateFormat in sessionStorage so re-entering Activity (e.g. on mobile) shows the correct
 * date format immediately without a brief blink from default → loaded value.
 */

import { useEffect, useState } from "react";
import type React from "react";
import { db } from "../../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { WorkspaceSettings as WorkspaceSettingsType, ValuesVisibilityMode, WorkspacePermissions, CustomRole, TimeFormat, DateFormat, ThemeMode } from "../../../types";

const DATE_FORMAT_CACHE_KEY_PREFIX = "workspace_dateFormat_";

function getCachedDateFormat(workspaceId: string): DateFormat {
  if (typeof window === "undefined" || !workspaceId) return "MM/DD/YYYY";
  try {
    const cached = sessionStorage.getItem(`${DATE_FORMAT_CACHE_KEY_PREFIX}${workspaceId}`);
    return cached === "DD/MM/YYYY" ? "DD/MM/YYYY" : "MM/DD/YYYY";
  } catch {
    return "MM/DD/YYYY";
  }
}

function setCachedDateFormat(workspaceId: string, value: DateFormat): void {
  if (typeof window === "undefined" || !workspaceId) return;
  try {
    sessionStorage.setItem(`${DATE_FORMAT_CACHE_KEY_PREFIX}${workspaceId}`, value);
  } catch {
    // ignore
  }
}

export interface UseWorkspaceSettingsParams {
  workspaceId: string;
}

export interface UseWorkspaceSettingsResult {
  valuesVisibilityMode: ValuesVisibilityMode;
  permissions: WorkspacePermissions | null;
  customRoles: CustomRole[];
  customRoleNames: Record<string, string>;
  timeFormat: TimeFormat;
  dateFormat: DateFormat;
  themeMode: ThemeMode;
  transactionIdPrefix: string;
  /** Next sequence for new transactions (for showing "next ID" in create mode). Undefined when prefix is empty. */
  transactionIdNextSequence: number | undefined;
  allowExpenseFee: boolean;
  allowTransferFee: boolean;
  setValuesVisibilityMode: React.Dispatch<React.SetStateAction<ValuesVisibilityMode>>;
  setPermissions: React.Dispatch<React.SetStateAction<WorkspacePermissions | null>>;
  setTimeFormat: React.Dispatch<React.SetStateAction<TimeFormat>>;
  setDateFormat: React.Dispatch<React.SetStateAction<DateFormat>>;
  setThemeMode: React.Dispatch<React.SetStateAction<ThemeMode>>;
  setTransactionIdPrefix: React.Dispatch<React.SetStateAction<string>>;
  setAllowExpenseFee: React.Dispatch<React.SetStateAction<boolean>>;
  setAllowTransferFee: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Hook for fetching workspace settings
 */
export const useWorkspaceSettings = ({
  workspaceId,
}: UseWorkspaceSettingsParams): UseWorkspaceSettingsResult => {
  const [valuesVisibilityMode, setValuesVisibilityMode] = useState<ValuesVisibilityMode>("manual");
  const [permissions, setPermissions] = useState<WorkspacePermissions | null>(null);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [customRoleNames, setCustomRoleNames] = useState<Record<string, string>>({});
  const [timeFormat, setTimeFormat] = useState<TimeFormat>("24h");
  const [dateFormat, setDateFormat] = useState<DateFormat>(() => getCachedDateFormat(workspaceId || ""));
  const [themeMode, setThemeMode] = useState<ThemeMode>("light");
  const [transactionIdPrefix, setTransactionIdPrefix] = useState<string>("");
  const [transactionIdNextSequence, setTransactionIdNextSequence] = useState<number | undefined>(undefined);
  const [allowExpenseFee, setAllowExpenseFee] = useState<boolean>(false);
  const [allowTransferFee, setAllowTransferFee] = useState<boolean>(false);

  // When workspaceId changes, restore dateFormat from cache immediately so Activity doesn't blink (e.g. DD/MM re-entering)
  useEffect(() => {
    if (workspaceId) {
      setDateFormat(getCachedDateFormat(workspaceId));
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;

    // Load workspace general settings and permissions
    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettingsType;
        setValuesVisibilityMode(data.valuesVisibilityMode || "manual");
        
        // Load time format (default to 24h for mobile compatibility, but can be changed)
        setTimeFormat(data.timeFormat || "24h");
        // Load date format (MM/DD/YYYY or DD/MM/YYYY); affects all transaction date displays
        const nextDateFormat = data.dateFormat === "DD/MM/YYYY" ? "DD/MM/YYYY" : "MM/DD/YYYY";
        setDateFormat(nextDateFormat);
        setCachedDateFormat(workspaceId, nextDateFormat);
        
        // Load theme mode (light or dark)
        setThemeMode(data.themeMode === "dark" ? "dark" : "light");
        
        // Load transaction ID prefix (user-friendly IDs e.g. DV-1, DEV-2)
        setTransactionIdPrefix(data.transactionIdPrefix ?? "");
        const prefix = data.transactionIdPrefix ?? "";
        setTransactionIdNextSequence(
          prefix && typeof prefix === "string" && prefix.trim() !== ""
            ? (typeof data?.transactionIdNextSequence === "number" ? data.transactionIdNextSequence : 1)
            : undefined
        );
        setAllowExpenseFee(data.allowExpenseFee === true);
        setAllowTransferFee(data.allowTransferFee === true);

        // Load permissions
        if (data.permissions) {
          setPermissions(data.permissions);
        } else {
          // Set default permissions
          setPermissions({
            manageWorkspaceConfig: ["owner", "admin"],
            archiveAccounts: ["owner"],
            deleteTransactions: ["owner"],
            editTransactions: ["owner", "admin"],
            editPreferences: ["owner", "admin"],
          });
        }

        // Load custom roles
        if (data.customRoles) {
          setCustomRoles(data.customRoles);
        } else {
          setCustomRoles([]);
        }

        // Load custom role names
        if (data.customRoleNames) {
          setCustomRoleNames(data.customRoleNames);
        } else {
          setCustomRoleNames({});
        }
      } else {
        // Default to manual mode if settings don't exist
        setValuesVisibilityMode("manual");
        // Default to 24h format
        setTimeFormat("24h");
        setDateFormat("MM/DD/YYYY");
        setCachedDateFormat(workspaceId, "MM/DD/YYYY");
        setThemeMode("light");
        setTransactionIdPrefix("");
        setTransactionIdNextSequence(undefined);
        setAllowExpenseFee(false);
        setAllowTransferFee(false);
        // Set default permissions
        setPermissions({
          manageWorkspaceConfig: ["owner", "admin"],
          archiveAccounts: ["owner"],
          deleteTransactions: ["owner"],
          editTransactions: ["owner", "admin"],
          editPreferences: ["owner", "admin"],
        });
        setCustomRoles([]);
        setCustomRoleNames({});
      }
    });

    return () => {
      unsubscribeSettings();
    };
  }, [workspaceId]);

  return {
    valuesVisibilityMode,
    permissions,
    customRoles,
    customRoleNames,
    timeFormat,
    dateFormat,
    themeMode,
    transactionIdPrefix,
    transactionIdNextSequence,
    allowExpenseFee,
    allowTransferFee,
    setValuesVisibilityMode,
    setPermissions,
    setTimeFormat,
    setDateFormat,
    setThemeMode,
    setTransactionIdPrefix,
    setAllowExpenseFee,
    setAllowTransferFee,
  };
};
